/* Brana Minimalistic 6.0.2 — semantic action links and shared UI behaviour. */
(() => {
  const SVG_NS = 'http://www.w3.org/2000/svg';

  const normalizePath = (path) => path.replace(/\/+$/, '') || '/';
  const normalizeHost = (host) => host.toLowerCase().replace(/^www\./, '').replace(/\.$/, '');
  const storefrontHosts = new Set([normalizeHost(window.location.hostname)]);
  const registerStorefront = (value) => {
    if (!value || typeof value !== 'string') return;
    try {
      const origin = value.includes('://') ? value : `https://${value}`;
      storefrontHosts.add(normalizeHost(new URL(origin, window.location.href).hostname));
    } catch (_) {}
  };
  [...(window.bmStorefrontOrigins || []), window.shopUrl,
    document.querySelector('link[rel="canonical"]')?.href].forEach(registerStorefront);

  const arrowKindForLink = (link) => {
    const href = (link.getAttribute('href') || '').trim();
    if (!href) return 'right';
    if (href.startsWith('#')) return 'down-right';
    try {
      const url = new URL(href, window.location.href);
      if (!/^https?:$/.test(url.protocol)) return 'up-right';
      if (!storefrontHosts.has(normalizeHost(url.hostname))) return 'up-right';
      // Ignore theme-preview transport parameters, not meaningful search data.
      const page = new URL(window.location.href);
      const comparableSearch = (search) => {
        const params = new URLSearchParams(search);
        ['preview_theme_id', '_fd', 'pb'].forEach((key) => params.delete(key));
        params.sort();
        return params.toString();
      };
      const sameDocument = normalizePath(url.pathname) === normalizePath(page.pathname)
        && comparableSearch(url.search) === comparableSearch(page.search);
      return sameDocument && url.hash ? 'down-right' : 'right';
    } catch (_) {
      return 'right';
    }
  };

  const makeArrowSvg = () => {
    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('viewBox', '0 0 16 16');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    const path = document.createElementNS(SVG_NS, 'path');
    path.setAttribute('d', 'M2.5 8h10M8.5 4l4 4-4 4');
    path.setAttribute('fill', 'none');
    path.setAttribute('stroke', 'currentColor');
    path.setAttribute('stroke-width', '1.25');
    path.setAttribute('stroke-linecap', 'square');
    path.setAttribute('stroke-linejoin', 'miter');
    svg.appendChild(path);
    return svg;
  };

  const hydrateArrow = (arrow, kind) => {
    if (!arrow) return;
    const resolvedKind = kind ||
      (arrow.classList.contains('bm-link-arrow--up-right') ? 'up-right' :
        arrow.classList.contains('bm-link-arrow--down-right') ? 'down-right' : 'right');
    arrow.className = `bm-link-arrow bm-link-arrow--${resolvedKind}`;
    if (arrow.dataset.bmArrowHydrated !== 'true') arrow.replaceChildren(makeArrowSvg());
    arrow.dataset.bmArrowHydrated = 'true';
  };

  const ensureLinkLabel = (link, arrow) => {
    const existing = Array.from(link.children).find((node) =>
      node !== arrow && node.classList?.contains('bm-link-label')
    );
    if (existing) return existing;

    const candidate = Array.from(link.children).find((node) =>
      node !== arrow && node.tagName === 'SPAN' && !node.hasAttribute('aria-hidden')
    );
    if (candidate) {
      candidate.classList.add('bm-link-label');
      return candidate;
    }

    const textNodes = Array.from(link.childNodes).filter((node) =>
      node.nodeType === Node.TEXT_NODE && node.nodeValue.trim()
    );
    if (!textNodes.length) return null;
    const label = document.createElement('span');
    label.className = 'bm-link-label';
    textNodes.forEach((node) => label.appendChild(node));
    link.insertBefore(label, arrow || link.firstChild);
    return label;
  };

  const decorateActionLink = (link) => {
    if (!link) return;
    let arrow = link.querySelector(':scope > .bm-link-arrow');
    if (!arrow) {
      arrow = Array.from(link.children).find((node) =>
        node.matches?.('span[aria-hidden="true"]') && node.querySelector('svg')
      ) || null;
    }
    if (!arrow) {
      arrow = document.createElement('span');
      arrow.setAttribute('aria-hidden', 'true');
      link.appendChild(arrow);
    }
    const kind = arrowKindForLink(link);
    hydrateArrow(arrow, kind);
    ensureLinkLabel(link, arrow);
    link.dataset.bmArrow = kind;
    link.dataset.bmArrowReady = 'true';
  };

  const decorateLinks = (scope = document) => {
    const selector = 'a.bm-text-link, a.bm-editorial-link, a.bm-footer__trustpilot';
    if (scope.matches?.(selector)) decorateActionLink(scope);
    scope.querySelectorAll?.(selector).forEach(decorateActionLink);
    scope.querySelectorAll?.('.bm-policy-toc .bm-link-arrow').forEach((arrow) => hydrateArrow(arrow, 'down-right'));
  };

  const normalisePhoneLinks = (scope = document) => {
    scope.querySelectorAll?.('a[href^="tel:"]').forEach((link) => {
      const digits = (link.getAttribute('href') || '').replace(/\D/g, '');
      if (digits !== '4593969394') return;
      link.setAttribute('href', 'tel:+4593969394');
      const visible = (link.textContent || '').trim();
      if (/^[+()\d\s.-]+$/.test(visible)) link.textContent = '(+45) 93 96 93 94';
    });
  };

  const init = (scope = document) => {
    decorateLinks(scope);
    normalisePhoneLinks(scope);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  /* Safari/iOS can restore a page from the back-forward cache with the DOM in
     its previous hydrated state. Re-resolve semantic arrows on pageshow so a
     stale direction can never survive a storefront/theme change. */
  window.addEventListener('pageshow', () => init());

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('esensera:full-search-rendered', () => init());
  if ('MutationObserver' in window) {
    const observer = new MutationObserver((records) => {
      for (const record of records) {
        if (record.type === 'attributes') {
          if (record.target.matches('a.bm-text-link, a.bm-editorial-link, a.bm-footer__trustpilot')) {
            decorateActionLink(record.target);
          }
        } else {
          record.addedNodes.forEach((node) => { if (node.nodeType === 1) decorateLinks(node); });
        }
      }
    });
    const observe = () => observer.observe(document.body, {
      childList: true, subtree: true, attributes: true, attributeFilter: ['href']
    });
    if (document.body) observe();
    else document.addEventListener('DOMContentLoaded', observe, { once: true });
  }
})();
